import { useState } from "react";
import { ACTIVITY_KIND_LABEL, UNITS, fmt, unitOf, type ActivityInput, type CalcLine, type Equipment, type Issue, type TemplateInput } from "@h2lca/core";
import { Button, Checkbox, Select, TextField } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { useWorkshop } from "@/store/workshop";
import { EfStatusBadge, ValueTypeBadge } from "../Badges";
import { efStatusLabel } from "../helpers";
import { IssueLine } from "../IssueList";
import { NumField } from "../NumField";

/** 같은 차원의 단위만 , kWh↔MJ 는 되고 kWh↔kg 는 단위 오류 */
function sameDimensionUnits(symbol: string) {
  const dim = unitOf(symbol)?.dimension;
  return Object.values(UNITS).filter((u) => !dim || u.dimension === dim);
}

const EXCLUDED_LABEL: Record<NonNullable<CalcLine["excluded"]>, string> = {
  parameter: "참고값. 계산에 쓰지 않음",
  missing: "자료 미입력. 계산 제외",
  no_ef: "배출계수 없음. LCI 기록, 계산 제외",
  unit: "단위 오류. 계산 제외",
  negative: "음수. 계산 제외",
};

/** 활동자료 한 항목 , 값,단위,유형,출처, 적용 배출계수, 계산식, 검증 메시지 */
export function InputCard({
  eq, input, line, tplInput, kgThrough, removable, issues,
}: {
  eq: Equipment;
  input: ActivityInput;
  line?: CalcLine;
  tplInput?: TemplateInput;
  kgThrough: number;
  removable: boolean;
  issues: Issue[];
}) {
  const updateInput = useWorkshop((s) => s.updateInput);
  const removeInput = useWorkshop((s) => s.removeInput);
  const [efOpen, setEfOpen] = useState(Boolean(input.customEf));
  const patch = (p: Partial<ActivityInput>) => updateInput(eq.id, input.id, p);
  const isParam = input.kind === "parameter";
  const missing = input.required && (input.amount === null || input.valueType === "missing");
  const denomUnits = sameDimensionUnits(input.unit);

  const resetDefault = () => {
    if (!tplInput) return;
    const amount = tplInput.defaultAmount === null ? null : tplInput.scaling === "perKgH2" ? tplInput.defaultAmount * kgThrough : tplInput.defaultAmount;
    patch({ amount, unit: tplInput.unit, valueType: amount === null ? "missing" : "default", source: tplInput.source });
  };
  const hint = tplInput?.computeHint;
  const hintParts = hint ? hint.product.map((k) => eq.inputs.find((i) => i.key === k)) : [];
  const canCompute = hint !== undefined && hintParts.every((p) => p && p.amount !== null);
  const compute = () => {
    if (!canCompute) return;
    const product = hintParts.reduce((a, p) => a * (p?.amount ?? 0), 1);
    patch({ amount: Math.round(product * 1000) / 1000, valueType: "estimated" });
  };

  return (
    <div className="input-card" data-missing={missing}>
      <div className="input-card-head">
        {removable ? (
          <span className="min-w-0 flex-1">
            <TextField label="항목명" value={input.label} onChange={(e) => patch({ label: e.target.value })} />
          </span>
        ) : (
          <span className="text-caption font-semibold text-ink">{input.label}</span>
        )}
        <span className="text-micro text-ink-subtle">{ACTIVITY_KIND_LABEL[input.kind]}</span>
        <ValueTypeBadge valueType={input.valueType} />
        {!input.required && <span className="badge-soft">선택</span>}
        {tplInput?.help && <InfoTip label={`${input.label} 설명`}>{tplInput.help}</InfoTip>}
        <span className="ml-auto flex items-center gap-label">
          {line && !line.excluded && (
            <span className="font-mono text-caption font-semibold text-ink">
              {fmt(line.kgCO2eq)} <span className="text-micro font-normal text-ink-muted">kgCO2eq</span>
            </span>
          )}
          {removable && (
            <Button type="button" size="compact" variant="danger" onClick={() => removeInput(eq.id, input.id)}>
              항목 제거
            </Button>
          )}
        </span>
      </div>

      <div className="input-card-grid">
        <NumField label="값" unit={input.unit} value={input.amount} min={0} onChange={(v) => patch({ amount: v })} placeholder={input.required ? "필수" : "선택"} />
        <label className="control-field">
          <span className="control-label">단위</span>
          <Select value={input.unit} onChange={(e) => patch({ unit: e.target.value })}>
            {denomUnits.map((u) => (
              <option key={u.symbol} value={u.symbol}>
                {u.symbol}, {u.label}
              </option>
            ))}
          </Select>
        </label>
        <TextField label="값 출처" value={input.source ?? ""} onChange={(e) => patch({ source: e.target.value || undefined })} placeholder="실측, 문헌, 기본값" />
        <div className="self-end">
          <Checkbox label="추정값으로 표시" checked={input.valueType === "estimated"} disabled={input.amount === null} onChange={(e) => patch({ valueType: e.target.checked ? "estimated" : input.amount === null ? "missing" : "user" })} />
        </div>
      </div>

      <div className="mt-item flex flex-wrap items-center gap-inline">
        {tplInput && (
          <Button type="button" size="compact" onClick={resetDefault}>
            기본값으로 되돌리기
          </Button>
        )}
        {hint && (
          <Button type="button" size="compact" disabled={!canCompute} onClick={compute} title={canCompute ? "" : "참고값이 모두 있어야 계산합니다"}>
            {hintParts.map((p) => p?.label ?? "?").join(" × ")} 로 채우기
          </Button>
        )}
        {!isParam && (
          <Button type="button" size="compact" aria-expanded={efOpen} onClick={() => setEfOpen((o) => !o)}>
            {input.customEf ? "직접 입력한 배출계수" : "배출계수 직접 입력"}
          </Button>
        )}
      </div>

      {efOpen && !isParam && (
        <div className="ws-factor-fields">
          <NumField label="계수 값" unit={`kgCO2eq/${input.customEf?.unit.split("/")[1] ?? input.unit}`} value={input.customEf?.value ?? null} min={0} onChange={(v) => patch({ customEf: v === null ? undefined : { value: v, unit: input.customEf?.unit ?? `kgCO2eq/${input.unit}`, source: input.customEf?.source || "사용자 입력", baseYear: input.customEf?.baseYear } })} />
          <label className="control-field">
            <span className="control-label">분모 단위</span>
            <Select value={input.customEf?.unit.split("/")[1] ?? input.unit} disabled={!input.customEf} onChange={(e) => input.customEf && patch({ customEf: { ...input.customEf, unit: `kgCO2eq/${e.target.value}` } })}>
              {denomUnits.map((u) => (
                <option key={u.symbol} value={u.symbol}>
                  {u.symbol}
                </option>
              ))}
            </Select>
          </label>
          <TextField label="계수 출처" value={input.customEf?.source ?? ""} disabled={!input.customEf} onChange={(e) => input.customEf && patch({ customEf: { ...input.customEf, source: e.target.value || "사용자 입력" } })} />
          <div className="flex items-end gap-label">
            <NumField label="기준연도" unit="년" value={input.customEf?.baseYear ?? null} disabled={!input.customEf} min={2000} step={1} onChange={(v) => input.customEf && patch({ customEf: { ...input.customEf, baseYear: v === null ? undefined : Math.round(v) } })} />
            {input.customEf && (
              <span><Button type="button" onClick={() => patch({ customEf: undefined })}>
                기본 계수
              </Button></span>
            )}
          </div>
        </div>
      )}

      {line && (
        <div className="input-card-ef">
          {line.ef ? (
            <>
              <span className="text-ink">
                적용 계수 <b className="font-mono">{fmt(line.ef.value, 4)}</b> {line.ef.unit}
              </span>
              <span>{line.ef.name}</span>
              <span>{line.ef.source}</span>
              {line.ef.baseYear > 0 && <span>기준연도 {line.ef.baseYear}</span>}
              <EfStatusBadge status={line.ef.status} />
              {line.ef.status === "placeholder" && <span className="text-tier-moderate">{efStatusLabel("placeholder").label}</span>}
            </>
          ) : (
            <span>{line.excluded ? EXCLUDED_LABEL[line.excluded] : "적용 계수 없음"}</span>
          )}
          {!line.excluded && line.formula && <span className="basis-full font-mono text-ink-muted">{line.formula}</span>}
        </div>
      )}

      {issues.length > 0 && (
        <div className="mt-item flex flex-col gap-label">
          {issues.map((i, k) => (
            <IssueLine key={`${i.code}-${k}`} issue={i} link={false} />
          ))}
        </div>
      )}
    </div>
  );
}
